// Grounded call summary, auto-tags, and voicemail provenance for the Calls ledger and CallDetail.
// Voicemail and outcome tags are derived strictly from current-call voicemail-drop records,
// server voicemail outcomes, or explicit staff notes recorded on THIS call.
// Pre-call CRM context_card fields are never used as current-call outcome or voicemail evidence,
// and short talk duration or PSTN "answered" status alone is never treated as voicemail.
import type { Call } from '@bcio-voice/contracts';
import { Voicemail } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import type { CallNote } from '../../lib/api.ts';
import { formatAbsolute, formatDuration } from '../../lib/format.ts';
import { type CallBlock, LIST_LABEL, callBlock } from '../blocklist/model.ts';
import { directionLabel, outcomeLabel } from '../phone/callView.ts';
import { type CallDrop, dropBadge, dropOutcomeLabel, dropResultText, playedText } from '../phone/voicemailDrop.ts';
import { CONFIDENCE_LABEL, terminationOf, terminationTag } from '../timeline/timeline.ts';

const SUMMARY_CSS = `
.call-summary-section { display: grid; gap: var(--space-2); padding: var(--space-3) var(--space-4); border: 1px solid var(--rule); border-radius: var(--radius-panel); background: var(--surface); }
.call-summary-lead { margin: 0; font-size: var(--text-body); line-height: var(--leading-body); color: var(--ink); }
.summary-tags { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-2); margin: 0; padding: 0; list-style: none; }
.row-badge.summary-tag { max-width: 100%; }
@media (max-width: 767px) {
  .call-summary-section { padding: var(--space-3); }
}
`;

export function CallSummaryStyles() {
  return <style href="sidetone-call-summary-inline" precedence="default">{SUMMARY_CSS}</style>;
}

export type VoicemailProvenanceKind =
  | 'voicemail_dropped'
  | 'voicemail_drop_interrupted'
  | 'voicemail_drop_in_progress'
  | 'voicemail_drop_failed'
  | 'inbound_voicemail'
  | 'left_voicemail';

export type VoicemailProvenanceSource =
  | 'voicemail_drop'
  | 'call_outcome'
  | 'call_note';

export interface VoicemailProvenance {
  isVoicemail: boolean;
  kind: VoicemailProvenanceKind | null;
  label: string | null;
  detail: string | null;
  source: VoicemailProvenanceSource | null;
}

export interface StructuredDisposition {
  code: string;
  label: string;
  isVoicemail: boolean;
  source: 'call_note';
  provenance: string;
}

export interface CallSummaryTag {
  id: string;
  label: string;
  title?: string | undefined;
  kind: 'outcome' | 'voicemail' | 'disposition' | 'termination' | 'followup';
  tone?: 'neutral' | 'warn' | 'end' | undefined;
}

export const STAFF_NOTE_PROVENANCE = 'Staff-reported in call note (not server-confirmed)';

const KNOWN_DISPOSITIONS: Record<string, { code: string; label: string; isVoicemail: boolean }> = {
  spoke_to_prospect: { code: 'spoke_to_prospect', label: 'Spoke to prospect', isVoicemail: false },
  'spoke to prospect': { code: 'spoke_to_prospect', label: 'Spoke to prospect', isVoicemail: false },
  left_voicemail: { code: 'left_voicemail', label: 'Left voicemail', isVoicemail: true },
  'left voicemail': { code: 'left_voicemail', label: 'Left voicemail', isVoicemail: true },
  voicemail: { code: 'left_voicemail', label: 'Left voicemail', isVoicemail: true },
  inbound_voicemail: { code: 'inbound_voicemail', label: 'Inbound voicemail', isVoicemail: true },
  'inbound voicemail': { code: 'inbound_voicemail', label: 'Inbound voicemail', isVoicemail: true },
  no_answer: { code: 'no_answer', label: 'No answer', isVoicemail: false },
  'no answer': { code: 'no_answer', label: 'No answer', isVoicemail: false },
  wrong_number: { code: 'wrong_number', label: 'Wrong number', isVoicemail: false },
  'wrong number': { code: 'wrong_number', label: 'Wrong number', isVoicemail: false },
  not_interested: { code: 'not_interested', label: 'Not interested', isVoicemail: false },
  'not interested': { code: 'not_interested', label: 'Not interested', isVoicemail: false },
  callback_requested: { code: 'callback_requested', label: 'Callback requested', isVoicemail: false },
  'callback requested': { code: 'callback_requested', label: 'Callback requested', isVoicemail: false },
  'call back': { code: 'callback_requested', label: 'Callback requested', isVoicemail: false },
  'send follow-up': { code: 'send_follow_up', label: 'Follow-up needed', isVoicemail: false },
};

function normalizeDispositionValue(raw: string | null | undefined, direction: Call['direction']): { code: string; label: string; isVoicemail: boolean } | null {
  if (!raw) return null;
  const cleaned = raw.trim().replace(/\.$/, '').toLowerCase();
  if (!cleaned) return null;
  const entry = KNOWN_DISPOSITIONS[cleaned];
  if (!entry) return null;
  if (entry.isVoicemail && direction === 'inbound') {
    return { code: 'inbound_voicemail', label: 'Inbound voicemail', isVoicemail: true };
  }
  return entry;
}

/**
 * Extracts an explicit staff-recorded disposition from notes saved on THIS call.
 * Pre-call CRM context_card fields are intentionally ignored because they can reflect
 * an older interaction rather than this call's outcome.
 */
export function extractStructuredDisposition(call: Call, notes: readonly CallNote[] = []): StructuredDisposition | null {
  // Blocked calls never take a staff disposition override.
  if (call.outcome === 'blocked' || callBlock(call)) return null;

  const sortedNotes = [...notes].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  for (const note of sortedNotes) {
    const body = note.body.trim();
    const dispMatch = body.match(/^Disposition:\s*(.+)$/im);
    if (dispMatch?.[1]) {
      const parsed = normalizeDispositionValue(dispMatch[1], call.direction);
      if (parsed) {
        if (parsed.isVoicemail && call.outcome !== 'answered') continue;
        return { ...parsed, source: 'call_note', provenance: STAFF_NOTE_PROVENANCE };
      }
    }
    const quick = normalizeDispositionValue(body, call.direction);
    if (quick) {
      if (quick.isVoicemail && call.outcome !== 'answered') continue;
      return { ...quick, source: 'call_note', provenance: STAFF_NOTE_PROVENANCE };
    }
  }

  return null;
}

/** Extracts a scheduled follow-up timestamp from post-call follow-up notes on this call, if present. */
export function extractScheduledFollowup(notes: readonly CallNote[] = []): string | null {
  const sortedNotes = [...notes].sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at));
  for (const note of sortedNotes) {
    const match = note.body.match(/^Follow-up:\s*(\S+)$/im);
    if (match?.[1] && !Number.isNaN(Date.parse(match[1]))) {
      return match[1];
    }
  }
  return null;
}

/**
 * Resolves whether a call has confirmed voicemail evidence (from server voicemail drop or outcome)
 * or a staff-reported voicemail disposition on this call.
 * Generic CRM context_card fields, short duration, or PSTN "answered" outcome alone are NEVER treated as voicemail.
 */
export function resolveVoicemailProvenance(
  call: Call,
  drop: CallDrop | null = null,
  notes: readonly CallNote[] = [],
): VoicemailProvenance {
  if (call.outcome === 'blocked' || callBlock(call)) {
    return { isVoicemail: false, kind: null, label: null, detail: null, source: null };
  }

  if (drop) {
    const fromRecord = Boolean((call as { voicemail_drop?: unknown }).voicemail_drop || drop.name || drop.startedAt);
    const source: VoicemailProvenanceSource = fromRecord ? 'voicemail_drop' : 'call_outcome';
    if (drop.phase === 'completed') {
      return {
        isVoicemail: true,
        kind: 'voicemail_dropped',
        label: drop.name ? 'Voicemail dropped' : 'Voicemail',
        detail: dropBadge(drop),
        source,
      };
    }
    if (drop.phase === 'interrupted') {
      return {
        isVoicemail: true,
        kind: 'voicemail_drop_interrupted',
        label: 'Voicemail drop interrupted',
        detail: dropBadge(drop),
        source,
      };
    }
    if (drop.phase === 'pending' || drop.phase === 'playing') {
      return {
        isVoicemail: true,
        kind: 'voicemail_drop_in_progress',
        label: 'Voicemail drop in progress',
        detail: dropBadge(drop),
        source: 'voicemail_drop',
      };
    }
    if (drop.phase === 'failed') {
      return {
        isVoicemail: true,
        kind: 'voicemail_drop_failed',
        label: 'Voicemail drop failed',
        detail: dropBadge(drop),
        source: 'voicemail_drop',
      };
    }
  }

  const rawOutcome = call.outcome as string | null;
  if (rawOutcome === 'voicemail') {
    const inbound = call.direction === 'inbound';
    return {
      isVoicemail: true,
      kind: inbound ? 'inbound_voicemail' : 'voicemail_dropped',
      label: inbound ? 'Inbound voicemail' : 'Voicemail',
      detail: inbound ? 'Server-recorded inbound voicemail outcome' : 'Server-recorded voicemail outcome',
      source: 'call_outcome',
    };
  }

  const disp = extractStructuredDisposition(call, notes);
  if (disp?.isVoicemail) {
    return {
      isVoicemail: true,
      kind: disp.code === 'inbound_voicemail' ? 'inbound_voicemail' : 'left_voicemail',
      label: disp.label,
      detail: STAFF_NOTE_PROVENANCE,
      source: 'call_note',
    };
  }

  return { isVoicemail: false, kind: null, label: null, detail: null, source: null };
}

/** Outcome label for the ledger and detail facts, differentiating inbound voicemail from drops and answered calls. */
export function callOutcomeText(call: Call, block: CallBlock | null, drop: CallDrop | null = null): string {
  if (block) return 'Blocked';
  if (drop) {
    if (!drop.name && drop.phase === 'completed') return 'Voicemail';
    return dropOutcomeLabel(drop);
  }
  if ((call.outcome as string | null) === 'voicemail' && call.direction === 'inbound') {
    return 'Inbound voicemail';
  }
  return outcomeLabel(call);
}

/** Builds a concise, strictly grounded summary sentence for CallDetail. */
export function buildCallSummaryText({
  call,
  block,
  drop,
  notes = [],
  selfUserId = null,
}: {
  call: Call;
  block: CallBlock | null;
  drop: CallDrop | null;
  notes?: readonly CallNote[] | undefined;
  selfUserId?: string | null | undefined;
}): string {
  const dirLabel = directionLabel(call, selfUserId ?? '');
  if (block) {
    const listText = block.list ? LIST_LABEL[block.list] : 'blocked list';
    return `Blocked ${dirLabel.toLowerCase()} call (${listText}).`;
  }
  if (drop) {
    const namePart = drop.name ? ` (${drop.name})` : '';
    const durPart = playedText(drop);
    if (drop.phase === 'completed') {
      return `Outbound call with voicemail drop${namePart} completed${durPart ? ` (${durPart})` : ''}.`;
    }
    if (drop.phase === 'interrupted') {
      return `Outbound call with voicemail drop${namePart} was interrupted before completion${durPart ? ` (${durPart.toLowerCase()})` : ''}.`;
    }
    if (drop.phase === 'pending' || drop.phase === 'playing') {
      return `Voicemail drop${namePart} is in progress; playback is not yet confirmed.`;
    }
    return `Voicemail drop${namePart} could not be completed.`;
  }

  const rawOutcome = call.outcome as string | null;
  if (rawOutcome === 'voicemail') {
    return call.direction === 'inbound' ? 'Inbound call reached voicemail.' : 'Call reached voicemail.';
  }

  const disp = extractStructuredDisposition(call, notes);
  const dirText = dirLabel.toLowerCase();

  if (call.outcome === 'answered') {
    const durationPart = call.talk_ms > 0 ? ` (${formatDuration(call.talk_ms)} talk time)` : '';
    if (disp) {
      return `Answered ${dirText} call${durationPart}; staff-reported note disposition: ${disp.label}.`;
    }
    return `Answered ${dirText} call${durationPart}.`;
  }
  if (call.outcome === 'missed' || rawOutcome === 'no_answer') {
    return call.direction === 'outbound' || rawOutcome === 'no_answer'
      ? 'Outbound call was not answered.'
      : 'Missed inbound call.';
  }
  if (call.outcome === 'busy') return 'The other side was busy.';
  if (call.outcome === 'rejected') return 'The call was declined.';
  if (call.outcome === 'cancelled') return 'The call was cancelled before answer.';
  if (call.outcome === 'failed' || call.state === 'failed') return 'The call could not be completed.';
  return `${dirLabel} call (${callOutcomeText(call, block, drop).toLowerCase()}).`;
}

/** Derives grounded auto-tags for CallDetail without duplicating badges or inventing unverified outcomes. */
export function deriveCallSummaryTags({
  call,
  block,
  drop,
  notes = [],
  operator = false,
  timeZone,
}: {
  call: Call;
  block: CallBlock | null;
  drop: CallDrop | null;
  notes?: readonly CallNote[] | undefined;
  operator?: boolean | undefined;
  timeZone?: string | undefined;
}): CallSummaryTag[] {
  const tags: CallSummaryTag[] = [];
  const vm = resolveVoicemailProvenance(call, drop, notes);

  if (block) {
    tags.push({
      id: 'outcome-blocked',
      label: 'Blocked',
      title: block.reason ?? (block.list ? LIST_LABEL[block.list] : 'Blocked list'),
      kind: 'outcome',
      tone: 'end',
    });
  } else if (drop) {
    tags.push({
      id: `voicemail-${drop.phase}`,
      label: callOutcomeText(call, null, drop),
      title: dropResultText(drop),
      kind: 'voicemail',
      tone: drop.phase === 'interrupted' || drop.phase === 'failed' ? 'warn' : 'neutral',
    });
    if (drop.name) {
      tags.push({
        id: 'voicemail-drop-detail',
        label: dropBadge(drop),
        title: dropResultText(drop),
        kind: 'voicemail',
      });
    }
  } else if (vm.isVoicemail && vm.source === 'call_outcome') {
    tags.push({
      id: `voicemail-${vm.kind}`,
      label: vm.label ?? 'Voicemail',
      title: vm.detail ?? undefined,
      kind: 'voicemail',
    });
  } else {
    const primaryLabel = callOutcomeText(call, block, drop);
    tags.push({
      id: `outcome-${call.outcome ?? call.state}`,
      label: primaryLabel,
      kind: 'outcome',
      tone: call.outcome === 'missed' || call.state === 'failed' ? 'end' : 'neutral',
    });
  }

  const disp = extractStructuredDisposition(call, notes);
  if (disp) {
    const alreadyShown = tags.some(t => t.label.toLowerCase() === disp.label.toLowerCase());
    if (!alreadyShown) {
      tags.push({
        id: `disposition-${disp.code}`,
        label: `${disp.label} (staff note)`,
        title: disp.provenance,
        kind: disp.isVoicemail ? 'voicemail' : 'disposition',
      });
    }
  }

  const followupAt = extractScheduledFollowup(notes);
  if (followupAt) {
    tags.push({
      id: 'followup-scheduled',
      label: 'Follow-up scheduled',
      title: `Staff-scheduled follow-up in call note for ${formatAbsolute(followupAt, timeZone)}`,
      kind: 'followup',
    });
  }

  if (!block && !drop && operator) {
    const term = terminationTag(terminationOf(call));
    if (term) {
      const conf = term.confidence && term.confidence !== 'observed' ? ` (${CONFIDENCE_LABEL[term.confidence]})` : '';
      tags.push({
        id: 'termination-tag',
        label: term.text,
        title: `${term.text}${conf}`,
        kind: 'termination',
        tone: term.icon === 'network' || term.icon === 'media' || term.icon === 'limit' ? 'warn' : 'neutral',
      });
    }
  }

  return tags;
}

export function CallSummarySection({
  call,
  block,
  drop,
  notes = [],
  operator = false,
  selfUserId = null,
  timeZone,
}: {
  call: Call;
  block: CallBlock | null;
  drop: CallDrop | null;
  notes?: readonly CallNote[] | undefined;
  operator?: boolean | undefined;
  selfUserId?: string | null | undefined;
  timeZone?: string | undefined;
}) {
  const summaryText = buildCallSummaryText({ call, block, drop, notes, selfUserId });
  const tags = deriveCallSummaryTags({ call, block, drop, notes, operator, timeZone });
  return (
    <section className="detail-section call-summary-section" aria-labelledby="call-summary-title">
      <CallSummaryStyles />
      <h3 id="call-summary-title" className="section-title">Call summary</h3>
      <p className="call-summary-lead">{summaryText}</p>
      <ul className="summary-tags" aria-label="Call summary tags">
        {tags.map(tag => (
          <li key={tag.id}>
            <span
              className={`row-badge summary-tag${tag.kind === 'voicemail' ? ' vmd-badge' : tag.tone === 'warn' ? ' tone-warn' : ''}`}
              title={tag.title}
            >
              {tag.kind === 'voicemail' ? <><Icon icon={Voicemail} /> <span>{tag.label}</span></> : <span>{tag.label}</span>}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
